import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, makeStyles, Typography } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { DeleteOutline } from '@material-ui/icons';
import { colors } from 'theme';
import { Buyer, DialogWidth, Material } from 'types';
import { useMaterials } from 'hooks';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import { CancelIcon, ValidateIcon } from 'components/Icons';
import { AppContext } from 'context';
import { transformId } from 'services/apiPlatformTransformer';
import { StreamCommand } from 'pages/Referentiels/Buyers/types';
import NewDialog from '../NewDialog';
import ClosureErrorIcon from '../Icons/ClosureErrorIcon';
import { DEFAULT_ITEMS_PER_SELECT } from '../../tableFilterContext';

const useStyles = makeStyles(theme => ({
  divider: {
    margin: '10px 34px 10px 11px',
    height: '60%',
  },
  streamName: {
    width: '30%',
  },
  collapsableDivider: {
    margin: '10px 0 8px 0',
  },
  material: {
    marginLeft: '24px',
    display: 'flex',
    alignItems: 'center',
  },
  materialsList: {
    marginLeft: '19px',
  },
  selectInput: {
    padding: '7px 0px 0px 9px',
    marginRight: 10,
    height: 31,
    display: 'flex',
    flexAlign: 'center',
  },
  materialLabel: {
    fontWeight: 600,
    fontSize: 14,
    marginRight: 33,
  },
  actions: {
    flexGrow: 1,
    textAlign: 'right',
  },
  saveIcon: {
    width: 15,
    height: 15,
    color: '#148192',
  },
  closeIcon: {
    width: 15,
    height: 15,
  },
  actionButton: {
    padding: 5,
    margin: 0,
    marginRight: 15,
    minWidth: 10,
    '&:disabled': {
      color: '#CFD4DA',
      '& *': {
        color: '#CFD4DA',
      },
    },
  },
  materialsCard: {
    padding: '23px 0 48px 39px',
  },
  cardTitle: {
    justifyContent: 'space-between',
    paddingLeft: '0.3em',
    fontSize: '24px',
    marginBottom: '15px',
    paddingBottom: '5px',
    marginRight: '27px',
    color: colors.primary,
    borderBottom: `1px solid ${colors.greyLighten10}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '48px',
  },
  selectMaterials: {
    marginLeft: '1em',
  },
  addMaterialsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  materialError: {
    fontSize: '0.75rem',
    fontWeight: 100,
    lineHeight: 1.66,
    color: '#f44336',
    marginRight: 20,
  },
  content: {
    marginBottom: '32px',
    marginRight: '32px',

    '& p': {
      fontSize: 16,
      lineHeight: '24px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& p span': {
      color: '#FF9500',
    },
  },
  bottomButton: {
    textAlign: 'right',
  },
  continueIcon: {
    width: '16px',
  },
  continueButton: {
    backgroundColor: '#2F3741',
    color: '#FFFFFF',
    marginLeft: theme.spacing(2),
    '&:hover': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
    '&:active': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
  },
}));

type StreamCreateFormProps = {
  onCancel: () => void;
  onSubmit: (stream: StreamCommand) => void;
  buyer: Buyer;
};

const BuyerMaterialEditForm: React.FC<StreamCreateFormProps> = ({ onCancel, onSubmit, buyer }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const materials = useMaterials({
    itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT),
    buyerId: transformId(buyer['@id']),
  });
  const [materialOptions, setMaterialOptions] = useState<Material[]>([]);
  const [streamComposition, setStreamComposition] = useState<Material[]>(buyer.acceptedMaterials);
  const [reject, setReject] = useState<Material | null>(null);
  const { t: pt } = useTranslation('translation', { keyPrefix: 'buyers' });
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const [open, setOpen] = useState(false);
  const materialsDeleted = buyer.acceptedMaterials.filter(object1 => {
    return !streamComposition.some(object2 => {
      return object1['@id'] === object2['@id'];
    });
  });

  useEffect(() => {
    if (!materials) {
      setMaterialOptions([]);
      setReject(null);
      return;
    }

    setMaterialOptions(
      materials.filter(
        material => !streamComposition?.filter(currentMaterial => currentMaterial['@id'] === material['@id']).length,
      ),
    );
  }, [materials, streamComposition, setMaterialOptions, setReject]);

  const addMaterial = useCallback(
    (materialId: string) => {
      if (!materials) return;
      const selectedMaterial = materials.find(material => material['@id'] === materialId);
      if (!selectedMaterial) return;
      setStreamComposition(
        [...streamComposition, selectedMaterial].sort((a, b) => a.shortName.localeCompare(b.shortName)),
      );
    },
    [materials, streamComposition, setStreamComposition],
  );

  const removeMaterial = useCallback(
    (materialToRemove: Material) => {
      setStreamComposition(streamComposition.filter(material => material !== materialToRemove));
    },
    [streamComposition, setStreamComposition],
  );

  const { control, handleSubmit, setValue } = useForm<StreamCommand>({
    mode: 'onChange',
    defaultValues: {
      material: '',
    },
  });

  const generateStreamAndSubmit = ({ material }: StreamCommand) => {
    if (materialsDeleted.length > 0 && !open) {
      setOpen(true);
    } else {
      onSubmit({
        material,
        composition: (reject !== null ? [...streamComposition, reject] : streamComposition).map(
          material => material['@id'],
        ),
        sortingCenter: sortingCenter?.['@id'],
      });
    }
  };

  return (
    <>
      <NewDialog
        closable={true}
        isOpen={open}
        onClose={() => setOpen(false)}
        width={DialogWidth.LARGE_FORM}
        title={pt('delete.dialog.title')}
        renderContent={() => (
          <div className={classes.content}>
            <Typography>{pt('delete.dialog.content')}</Typography>
            <br></br>
            <Typography>{pt('delete.dialog.content2')}</Typography>
          </div>
        )}
        renderFooter={() => (
          <div className={classes.bottomButton}>
            <Button
              variant="outlined"
              startIcon={<ClosureErrorIcon className={classes.closeIcon} />}
              onClick={() => setOpen(false)}
            >
              {pt('delete.dialog.button.cancel')}
            </Button>
            <Button
              className={classes.continueButton}
              variant="outlined"
              endIcon={<ValidateIcon className={classes.continueIcon} />}
              onClick={handleSubmit(generateStreamAndSubmit)}
            >
              {pt('delete.dialog.button.yes')}
            </Button>
          </div>
        )}
      />

      <form onSubmit={handleSubmit(generateStreamAndSubmit)}>
        <Typography variant="h2" className={classes.cardTitle}>
          <div className={classes.addMaterialsContainer}>
            <div>{pt('materials')}</div>
            <div className={classes.selectMaterials}>
              <Controller
                name="material"
                control={control}
                render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
                  <SelectWithErrorField
                    id="material"
                    choices={materialOptions}
                    choiceDisplayKey="shortName"
                    error={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    placeholder={t('streams.create.materialChoice')}
                    inputRef={ref}
                    classes={{ root: classes.selectInput }}
                    disabled={materialOptions.length === 0}
                    onChange={e => {
                      onChange(e);
                      setValue('material', '');
                      addMaterial(e.target.value as string);
                    }}
                    {...inputProps}
                  />
                )}
              />
            </div>
          </div>
          <div>
            <span className={classes.materialError}>
              {streamComposition.length === 0 && <>{pt('material.error')}</>}
            </span>
            <Button classes={{ root: classes.actionButton }} onClick={onCancel}>
              <CancelIcon className={classes.closeIcon} />
            </Button>
            <Button type={'submit'} disabled={streamComposition.length === 0} className={classes.actionButton}>
              <ValidateIcon className={classes.saveIcon} />
            </Button>
          </div>
        </Typography>
        {streamComposition
          .sort(function (a: Material, b: Material) {
            return a.shortName === 'Refus' && b.shortName !== 'Refus' ? 1 : -1;
          })
          .map(material =>
            material.hasExpeditions ? (
              <Chip key={material['@id']} label={material.shortName} />
            ) : (
              <Chip
                key={material['@id']}
                label={material.shortName}
                onDelete={() => {
                  removeMaterial(material);
                }}
                deleteIcon={<DeleteOutline style={{ color: colors.white }} />}
              />
            ),
          )}
      </form>
    </>
  );
};

export default BuyerMaterialEditForm;
